/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IProgressIndicatorProps, IProgressIndicatorStyles, ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';

export type NotificationsIconProps = IButtonProps & {
    /**
     * The number to display in the badge.
     */
    count: number;

    /**
     * A flag to control the visibility
     * of the progress bar.
     */
    isInProgress: boolean;
};

const headerTheme = getScopedTheme('NotificationsIcon');
const { styleComponent } = getStylist('NotificationsIcon');

const ProgressBar = styleComponent<IProgressIndicatorProps>(ProgressIndicator)('ProgressBar', {
    position: 'absolute',
    width: '100%',
    bottom: 0,
    left: 0
});
const progressBarStyles = classNamesFunction<IProgressIndicatorProps, IProgressIndicatorStyles>()({
    progressTrack: { background: 'transparent' },
    progressBar: { background: headerTheme.progressBarColor, animationDuration: '1.5s', animationTimingFunction: 'linear' }
});
const getBadgeStyles = (count: number) => {
    const isThreeDigit = count > 99;

    return classNamesFunction<IButtonProps, IButtonStyles>()({
        root: {
            selectors: {
                '&::after': {
                    top: 4,
                    position: 'absolute',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    left: isThreeDigit ? '40%' : '50%',
                    fontWeight: headerTheme.badgeFontWeight,
                    color: headerTheme.badgeForegroundColor,
                    content: `"${isThreeDigit ? '99+' : count}"`,
                    backgroundColor: headerTheme.badgeBackgroundColor,
                    fontSize: isThreeDigit ? headerTheme.badgeTinyFontSize : headerTheme.badgeFontSize
                }
            }
        }
    });
};

/**
 * Renders the notification icon in the logged in
 * header. Displays notifications that are unseen and
 * are in progress.
 */
export const NotificationsIcon = observer((props: NotificationsIconProps) => {
    const { count, isInProgress, ...rest } = props;

    const mergedStyles = mergeStyleSets(count ? getBadgeStyles(count) : undefined, props.styles);

    return (
        <CommandBarButton
            {...rest}
            styles={mergedStyles}
            iconProps={{ iconName: 'Ringer' }}
            onRenderChildren={(buttonProps, defaultRender) => (
                <Stack verticalFill verticalAlign="space-between">
                    {defaultRender(buttonProps)}
                    {isInProgress ? <ProgressBar barHeight={3} styles={progressBarStyles} /> : null}
                </Stack>
            )}
        />
    );
});
